/**
 * @fileoverview Duty context callout for heads that share a statutory duty
 * @module jobs/phases/review/DutyContextBanner
 *
 * Purpose: when one duty is enforceable in more than one way, the same conduct
 * gets reviewed by a head in each workspace. Without this callout the two read
 * as unrelated claims. It names the root duty, states which consequence this
 * head covers and who can bring it, and links across to the other.
 *
 * Renders nothing for heads that are not part of a shared duty, so both
 * focused head views can mount it unconditionally.
 *
 * @component
 * @example
 * <DutyContextBanner
 *   jobId={jobId}
 *   workspace="recoverable"
 *   headKey={head.headKey}
 * />
 */

import { useNavigate } from "@tanstack/react-router";
import { ArrowRight, Scale } from "lucide-react";
import type { Id } from "@cvx/_generated/dataModel";
import { Callout } from "./flaggedTransactions/Callout";
import { StatuteBadge } from "./flaggedTransactions/AgentText";
import { dutyForHead, type DutyWorkspace } from "./duties";

export function DutyContextBanner(props: {
  jobId: Id<"jobs">;
  workspace: DutyWorkspace;
  headKey: string;
  className?: string;
}) {
  const { jobId, workspace, headKey, className } = props;
  const navigate = useNavigate();
  const resolved = dutyForHead(workspace, headKey);

  if (!resolved) return null;
  const { duty, thisLimb, otherLimbs } = resolved;

  return (
    <Callout
      tone="legal"
      title="Statutory duty"
      icon={Scale}
      className={className}
    >
      <p>
        <StatuteBadge>{duty.provision}</StatuteBadge> creates the{" "}
        {duty.name.toLowerCase()}. This head covers one consequence of breaching
        it, not a claim in its own right.
      </p>
      <p className="mt-1.5">
        <span className="font-medium">Here: {thisLimb.claimType}</span> under{" "}
        <StatuteBadge>{thisLimb.provision}</StatuteBadge>. {thisLimb.broughtBy}
      </p>
      {otherLimbs.map((limb) => (
        <p key={limb.workspace} className="mt-1.5">
          <span className="font-medium">
            Also assessed as: {limb.claimType}
          </span>{" "}
          under <StatuteBadge>{limb.provision}</StatuteBadge>. {limb.broughtBy}
        </p>
      ))}
      <p className="mt-1.5 text-muted-foreground">
        The same loss is assessed under each consequence. The recoverable amount
        is not duplicated across them.
      </p>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {otherLimbs.map((limb) => (
          <button
            key={limb.workspace}
            type="button"
            className="inline-flex items-center gap-1 rounded-md border bg-background px-2 py-0.5 text-xs font-medium transition-colors hover:border-foreground/30 hover:bg-muted/60"
            onClick={() =>
              navigate({
                to: "/dashboard/jobs/$jobId/review/$agent",
                params: { jobId: String(jobId), agent: limb.agentSlug },
                search: { head: limb.headKey },
              })
            }
          >
            Open in {limb.workspaceLabel}
            <ArrowRight className="h-3 w-3 text-muted-foreground" />
          </button>
        ))}
      </div>
    </Callout>
  );
}
